import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Star, 
  Calendar, 
  User, 
  BookOpen, 
  Users,
  Plus,
  Trash2,
  Edit3,
  Globe,
  Lock,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { DinoAvatar } from '@/components/DinoAvatar';
import { StarRating } from '@/components/StarRating';
import { ShareButton } from '@/components/ShareButton';
import { VoiceReview } from '@/components/VoiceReview';
import { AuthorHeader } from '@/components/AuthorHeader';
import { CoverImage } from '@/components/CoverImage';
import { PairwiseSheet } from '@/components/PairwiseSheet';
import { PrivacyIndicator } from '@/components/PrivacyIndicator';
import { SaveToTBRButton } from '@/components/SaveToTBRButton';
import { TBRRowContent } from '@/components/TBRRowContent';
import { BookIdentityFixer } from '@/components/BookIdentityFixer';
import { EditionMergeDialog } from '@/components/EditionMergeDialog';
import { MarkAsUnfinishedDialog } from '@/components/MarkAsUnfinishedDialog';
import { UnfinishedRowContent } from '@/components/UnfinishedRowContent';
import { AddPageCountDialog } from '@/components/AddPageCountDialog';
import { ExternalShareButton } from '@/components/ExternalShareButton';
import { BookTagsSection } from '@/components/BookTagsSection';
import { ReviewCard } from '@/components/ReviewCard';
import { BookReviewFilters } from '@/components/BookReviewFilters';
import { TagSelector } from '@/components/TagSelector';
import { BookReviewStats } from '@/components/BookReviewStats';
import { SeriesContextBanner } from '@/components/SeriesContextBanner';
import { StartDinoDuoButton } from '@/components/StartDinoDuoButton';
import { ContentWarningsSection } from '@/components/ContentWarningsSection';
import { SafeHtml } from '@/components/SafeHtml';
import { useAuth } from '@/hooks/useAuth';
import { usePairwiseRanking } from '@/hooks/usePairwiseRanking';
import { useSaveToTBR } from '@/hooks/useSaveToTBR';
import { useBookReviews } from '@/hooks/useBookReviews';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { enrichSpecificBook } from '@/lib/enrichmentUtils';

interface Book {
  id: string;
  title: string;
  subtitle?: string;
  authors: string[];
  published_year?: number;
  cover_url?: string;
  synopsis?: string;
  page_count?: number;
  page_count_source?: string;
  page_count_estimated?: boolean;
  isbn10?: string;
  isbn13?: string;
}

interface UserBook {
  id?: string;
  rating_star?: number;
  review_text?: string;
  read_at?: string;
  shelf?: string;
  privacy?: string;
  started_at?: string;
  finished_at?: string;
  created_at?: string;
  is_purchased?: boolean;
  current_page?: number;
  total_pages?: number;
  reading_progress_percent?: number;
}

export default function BookDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const reviewRef = useRef<HTMLDivElement>(null);

  const [book, setBook] = useState<Book | null>(null);
  const [userBook, setUserBook] = useState<UserBook | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [showIdentityFixer, setShowIdentityFixer] = useState(false);
  const [showEditionMerge, setShowEditionMerge] = useState(false);
  const [showDNFDialog, setShowDNFDialog] = useState(false);
  const [showAddPageCount, setShowAddPageCount] = useState(false);
  
  // Review filter and sort state
  const [reviewFilter, setReviewFilter] = useState<number | null>(null);
  const [reviewSort, setReviewSort] = useState<'recent' | 'popular'>('recent');

  // Form state
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [startedAt, setStartedAt] = useState<Date | undefined>();
  const [finishedAt, setFinishedAt] = useState<Date | undefined>();
  const [startedMonth, setStartedMonth] = useState<Date | undefined>();
  const [finishedMonth, setFinishedMonth] = useState<Date | undefined>();
  const [readAt, setReadAt] = useState('');
  const [privacy, setPrivacy] = useState('private');
  const [markAsRead, setMarkAsRead] = useState(false);
  const [useVoiceReview, setUseVoiceReview] = useState(false);

  // Pairwise ranking hook
  const { pairwiseData, loading: pairwiseLoading, triggerPairwiseComparison, clearPairwiseData } = usePairwiseRanking();
  
  // TBR hook
  const { isSaved, toggleSave, loading: tbrLoading } = useSaveToTBR(id || '');
  
  // Book reviews hook
  const { 
    filteredReviews, 
    stats, 
    loading: reviewsLoading,
    refreshReviews 
  } = useBookReviews(id || '', reviewFilter, reviewSort);

  useEffect(() => {
    if (id) {
      loadBookData();
    }
  }, [id, user]);

  // Load default privacy from profile settings
  useEffect(() => {
    const loadDefaultPrivacy = async () => {
      if (!user || userBook) return; // Only set default for new reviews
      
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('privacy_mode')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('[BookDetail] Error loading profile privacy:', error);
          return;
        }

        if (profile) {
          // Map profile privacy_mode to book privacy
          const defaultPrivacy = profile.privacy_mode === 'ghost' ? 'private' : profile.privacy_mode;
          setPrivacy(defaultPrivacy);
        }
      } catch (error) {
        console.error('[BookDetail] Error loading default privacy:', error);
      }
    };

    loadDefaultPrivacy();
  }, [user, userBook]);

  // Auto-enrich books without synopsis
  useEffect(() => {
    if (book && !book.synopsis && !enriching && user) {
      const autoEnrich = async () => {
        console.log('[BookDetail] Auto-enriching book without synopsis:', book.id);
        setEnriching(true);
        try {
          await enrichSpecificBook(book.id);
          // Reload after enrichment attempt
          setTimeout(() => {
            loadBookData();
          }, 3000);
        } catch (error) {
          console.error('[BookDetail] Auto-enrichment failed:', error);
        } finally {
          setEnriching(false);
        }
      };
      
      // Small delay to avoid immediate enrichment on page load
      const timer = setTimeout(autoEnrich, 1000);
      return () => clearTimeout(timer);
    }
  }, [book?.id, book?.synopsis, user]);

  // Handle deep linking to specific reviews
  useEffect(() => {
    const reviewId = searchParams.get('review');
    const chatId = searchParams.get('chat');
    
    if (reviewId && reviewRef.current) {
      // Scroll to specific review after a brief delay to ensure content is loaded
      const timer = setTimeout(() => {
        const reviewElement = document.getElementById(`review-${reviewId}`);
        if (reviewElement) {
          reviewElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight effect
          reviewElement.classList.add('ring-2', 'ring-primary', 'ring-opacity-50');
          setTimeout(() => {
            reviewElement.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50');
          }, 3000);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
    
    // Show chat banner if coming from chat without specific review
    if (chatId && !reviewId) {
      toast({
        title: 'Shared from chat',
        description: 'This book was shared with you',
      });
    }
  }, [searchParams, filteredReviews]);

  const loadBookData = async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Reset all state at the beginning to prevent state leak between books
      setUserBook(null);
      setRating(0);
      setReview('');
      setStartedAt(undefined);
      setFinishedAt(undefined);
      setReadAt('');
      setPrivacy('private');
      setEditing(false);

      // Load book details
      const { data: bookData, error: bookError } = await supabase
        .from('books')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (bookError) throw bookError;
      
      if (!bookData) {
        // Book not found in database
        setBook(null);
        return;
      }

      // Check for canonical ISBN conflict: if this book has a canonical_isbn13 
      // and another identity_locked book with the same canonical_isbn13 exists,
      // redirect to the canonical one
      if (bookData.canonical_isbn13 && !bookData.identity_locked) {
        const { data: canonicalBook } = await supabase
          .from('books')
          .select('id')
          .eq('canonical_isbn13', bookData.canonical_isbn13)
          .eq('identity_locked', true)
          .neq('id', id)
          .maybeSingle();

        if (canonicalBook) {
          console.log('[BookDetail] Redirecting to canonical book:', canonicalBook.id);
          navigate(`/book/${canonicalBook.id}`, { replace: true });
          return;
        }
      }
      
      setBook(bookData);

      if (user) {
        // Load user's rating for this book
        const { data: userBookData } = await supabase
          .from('user_books')
          .select('*')
          .eq('user_id', user.id)
          .eq('book_id', id)
          .maybeSingle();

        if (userBookData) {
          setUserBook(userBookData);
          setRating(userBookData.rating_star || 0);
          setReview(userBookData.review_text || '');
          setStartedAt(userBookData.started_at ? new Date(userBookData.started_at) : undefined);
          setFinishedAt(userBookData.finished_at ? new Date(userBookData.finished_at) : undefined);
          setReadAt(userBookData.read_at || '');
          setPrivacy(userBookData.privacy || 'private');
        }
        // Note: No else clause needed as state is already reset above
      }
    } catch (error) {
      console.error('Error loading book data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not load book details. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetryEnrichment = async () => {
    if (!book) return;
    setEnriching(true);
    try {
      await enrichSpecificBook(book.id);
      toast({
        title: 'Enrichment started',
        description: 'We’re fetching synopsis and details...'
      });
      setTimeout(() => {
        loadBookData();
      }, 2500);
    } catch (error) {
      console.error('[BookDetail] Manual enrichment failed:', error);
      toast({
        variant: 'destructive',
        title: 'Enrichment failed',
        description: 'Please try again in a moment.'
      });
    } finally {
      setEnriching(false);
    }
  };

  const handleSaveRating = async () => {
    if (!user || !book) return;

    try {
      setSaving(true);

      // Moderate review text if present
      if (review.trim()) {
        const { data: moderationResult, error: moderationError } = await supabase.functions.invoke('moderate-content', {
          body: { text: review.trim() }
        });

        if (!moderationError && moderationResult?.flagged) {
          toast({
            variant: "destructive",
            title: "Review contains inappropriate content",
            description: "Your review contains content that violates our guidelines. Please revise and try again.",
          });
          setSaving(false);
          return;
        }
      }

      // Determine the read_at value
      let finalReadAt: string | null = null;
      if (readAt) {
        finalReadAt = readAt;
      } else if (markAsRead) {
        finalReadAt = new Date().toISOString().split('T')[0];
      }

      // Use finishedAt if provided, otherwise use finalReadAt
      let finalFinishedAt: string | null = (finishedAt ? finishedAt.toISOString().split('T')[0] : null) || finalReadAt || null;

      // Determine appropriate shelf based on read status and rating
      let shelf: string | undefined = undefined;
      if (finalFinishedAt || rating > 0) {
        shelf = 'read';
      }

      const bookData = {
        user_id: user.id,
        book_id: book.id,
        rating_star: rating,
        review_text: review.trim() || null,
        ...(startedAt && { started_at: startedAt.toISOString().split('T')[0] }),
        ...(shelf === 'read' && finalFinishedAt && { finished_at: finalFinishedAt }),
        privacy: privacy,
        ...(shelf && { shelf }),
      };

      // Insert or update without relying on unique constraints
      const { data: existingRow, error: lookupError } = await supabase
        .from('user_books')
        .select('id')
        .eq('user_id', user.id)
        .eq('book_id', book.id)
        .maybeSingle();

      if (lookupError) throw lookupError;

      let data;
      if (existingRow?.id) {
        const { data: updated, error: updateError } = await supabase
          .from('user_books')
          .update(bookData)
          .eq('id', existingRow.id)
          .select()
          .single();
        if (updateError) throw updateError;
        data = updated;
      } else {
        const { data: inserted, error: insertError } = await supabase
          .from('user_books')
          .insert(bookData)
          .select()
          .single();
        if (insertError) throw insertError;
        data = inserted;
      }

      // Log activity for followers (respect review vs rating)
      try {
        let activityType: string | null = null;
        if (privacy === 'public') {
          if (review.trim()) {
            activityType = userBook?.id ? 'review_updated' : 'review_created';
          } else if (rating > 0) {
            activityType = userBook?.id ? 'rating_updated' : 'rating_created';
          }
        }

        if (activityType) {
          await supabase
            .from('activities')
            .insert({
              user_id: user.id,
              type: activityType,
              book_id: book.id,
              book_title: book.title,
              book_cover_url: book.cover_url,
              payload: {
                book_id: book.id,
                book_title: book.title,
                rating_star: rating || null,
                review_excerpt: review.trim() ? review.trim().slice(0, 200) : null
              }
            });
        }
      } catch (activityError) {
        console.warn('Activity insert failed (non-blocking):', activityError);
      }

      // Save tags to book_tags table
      if (selectedTags.length > 0) {
        try {
          // Delete existing tags for this user and book
          await supabase
            .from('book_tags')
            .delete()
            .eq('book_id', book.id)
            .eq('user_id', user.id);

          // Insert new tags
          const tagInserts = selectedTags.map(tag => ({
            book_id: book.id,
            user_id: user.id,
            tag_name: tag.toLowerCase().trim()
          }));

          await supabase
            .from('book_tags')
            .insert(tagInserts);

          // Update user_books.tags array
          await supabase
            .from('user_books')
            .update({ tags: selectedTags.map(t => t.toLowerCase().trim()) })
            .eq('id', data.id);

        } catch (tagError) {
          console.warn('Tag save failed (non-blocking):', tagError);
        }
      }

      setUserBook(data);
      setEditing(false);
      
      // Trigger pairwise comparison if rating is provided (before showing toast)
      let showedPairwise = false;
      if (rating > 0) {
        showedPairwise = await triggerPairwiseComparison(user.id, book.id, rating);
      }
      
      // Only show the success toast if pairwise comparison didn't trigger
      if (!showedPairwise) {
        toast({
          title: "Rating saved",
          description: "Your rating has been saved successfully.",
        });
      }
    } catch (error: any) {
      console.error('Error saving rating:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message ? String(error.message) : "Could not save your rating.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRating = async () => {
    if (!userBook?.id) return;

    try {
      await supabase
        .from('user_books')
        .delete()
        .eq('id', userBook.id);

      setUserBook(null);
      setRating(0);
      setReview('');
      setStartedAt(undefined);
      setFinishedAt(undefined);
      setReadAt('');
      setPrivacy('private');
      setEditing(false);

      toast({
        title: "Rating deleted",
        description: "Your rating has been removed.",
      });
    } catch (error) {
      console.error('Error deleting rating:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not delete your rating.",
      });
    }
  };

  const handleStartReading = async () => {
    if (!user || !book) return;

    try {
      const { error } = await supabase
        .from('user_books')
        .update({
          shelf: 'reading',
          started_at: new Date().toISOString().split('T')[0],
          total_pages: book.page_count || null,
          current_page: 0,
        })
        .eq('user_id', user.id)
        .eq('book_id', book.id);

      if (error) throw error;

      toast({
        title: "Started reading",
        description: "Book moved to Currently Reading.",
      });
      
      loadBookData();
    } catch (error) {
      console.error('Error starting book:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update reading status.",
      });
    }
  };

  const handleRemoveFromTBR = async () => {
    if (!user || !book) return;

    try {
      const { error } = await supabase
        .from('user_books')
        .delete()
        .eq('user_id', user.id)
        .eq('book_id', book.id)
        .eq('shelf', 'to-read');

      if (error) throw error;

      toast({
        title: "Removed from TBR",
        description: "Book removed from your To Be Read list.",
      });
      
      loadBookData();
    } catch (error) {
      console.error('Error removing from TBR:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not remove book from TBR.",
      });
    }
  };

  const handleTogglePurchased = async (isPurchased: boolean) => {
    if (!user || !book) return;
    
    try {
      const { error } = await supabase
        .from('user_books')
        .update({ is_purchased: isPurchased })
        .eq('user_id', user.id)
        .eq('book_id', book.id);
      
      if (error) throw error;
      
      // Update local state
      setUserBook(prev => prev ? { ...prev, is_purchased: isPurchased } : prev);
      
      toast({
        title: isPurchased ? "Marked as Owned" : "Marked as Not Purchased",
        description: isPurchased 
          ? "Book is now tracked as purchased/owned" 
          : "Book is now tracked as not purchased",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update status",
      });
    }
  };

  const handleMarkAsUnfinished = async (data: { currentPage?: number; reason: string }) => {
    if (!user || !book) return;
    
    try {
      setSaving(true);
      
      const totalPages = book.page_count || 0;
      const readingProgress = data.currentPage && totalPages > 0 
        ? Math.round((data.currentPage / totalPages) * 100) 
        : 0;

      const bookData = {
        user_id: user.id,
        book_id: book.id,
        shelf: 'unfinished',
        current_page: data.currentPage || null,
        total_pages: totalPages || null,
        reading_progress_percent: readingProgress,
        review_text: data.reason || null,
        started_at: userBook?.started_at || new Date().toISOString().split('T')[0],
      };

      // Check if book exists in user_books
      const { data: existingRow } = await supabase
        .from('user_books')
        .select('id')
        .eq('user_id', user.id)
        .eq('book_id', book.id)
        .maybeSingle();

      if (existingRow?.id) {
        await supabase
          .from('user_books')
          .update(bookData)
          .eq('id', existingRow.id);
      } else {
        await supabase
          .from('user_books')
          .insert(bookData);
      }

      // Log activity (non-blocking)
      try {
        await supabase.from('activities').insert({
          user_id: user.id,
          type: 'book_dnf',
          book_id: book.id,
          book_title: book.title,
          book_cover_url: book.cover_url,
          payload: {
            current_page: data.currentPage,
            total_pages: totalPages,
            progress_percent: readingProgress,
            reason: data.reason
          }
        });
      } catch (e) {
        console.warn('Activity insert failed (non-blocking):', e);
      }

      toast({
        title: "Marked as unfinished",
        description: "Book moved to Did Not Finish shelf.",
      });

      setShowDNFDialog(false);
      await loadBookData();
    } catch (error) {
      console.error('Error marking as unfinished:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not mark book as unfinished.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 bg-muted/40 rounded" />
              <div className="h-8 bg-muted/40 rounded w-48" />
            </div>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="aspect-[2/3] bg-muted/20 rounded-lg" />
              </div>
              <div className="lg:col-span-2 space-y-6">
                <div className="space-y-3">
                  <div className="h-8 bg-muted/40 rounded w-3/4" />
                  <div className="h-4 bg-muted/30 rounded w-1/2" />
                  <div className="h-4 bg-muted/30 rounded w-1/3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold mb-2">Book not found</h2>
            <p className="text-muted-foreground mb-4">The book you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/search')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Search
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">

      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Book Cover & Info */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="aspect-[2/3] overflow-hidden rounded-lg bg-muted/20 max-w-[200px] mx-auto">
                <CoverImage
                  bookId={book.id}
                  isbn13={book.isbn13}
                  isbn10={book.isbn10}
                  title={book.title}
                  author={book.authors?.[0]}
                  coverUrl={book.cover_url}
                  size="lg"
                  className="w-full h-full"
                />
              </div>

              {/* Book Metadata */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span>
                      {book.authors.map((author, index) => (
                        <React.Fragment key={author}>
                          <a 
                            href={`/author/${encodeURIComponent(author)}`}
                            className="hover:text-primary hover:underline transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/author/${encodeURIComponent(author)}`);
                            }}
                          >
                            {author}
                          </a>
                          {index < book.authors.length - 1 && ', '}
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                  
                  {book.published_year && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{book.published_year}</span>
                    </div>
                  )}

                  {book.page_count ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <BookOpen className="w-4 h-4" />
                      <span>
                        {book.page_count_estimated ? '~' : ''}{book.page_count} pages
                        {book.page_count_estimated && ' (estimated)'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm">
                      <BookOpen className="w-4 h-4 text-muted-foreground" />
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-sm text-primary hover:underline"
                        onClick={() => setShowAddPageCount(true)}
                      >
                        Add page count
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
             {/* Book Title & Description */}
             <div className="space-y-4">
                 <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h1 className="text-3xl font-heading font-bold mb-2">{book.title}</h1>
                    {book.subtitle && (
                      <p className="text-lg text-muted-foreground">{book.subtitle}</p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <StartDinoDuoButton bookId={book.id} />
                    <ShareButton
                      shareData={{
                        kind: 'book',
                        book_id: book.id,
                        book: {
                          id: book.id,
                          title: book.title,
                          authors: book.authors,
                          cover_url: book.cover_url
                        }
                      }}
                      variant="outline"
                    />
                  </div>
                </div>

              {/* Series Context Banner */}
              <SeriesContextBanner
                bookId={book.id}
                seriesName={(book as any).series_name}
                seriesPosition={(book as any).series_position}
                seriesTotal={(book as any).series_total}
                authors={book.authors}
              />

              {book.synopsis ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Synopsis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SafeHtml 
                      html={book.synopsis} 
                      className="text-muted-foreground leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                    />
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Synopsis</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {enriching ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                        <span className="text-sm">Searching for book details...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <p className="text-muted-foreground text-sm">
                          No synopsis found yet. Try again to fetch details.
                        </p>
                        <Button size="sm" onClick={handleRetryEnrichment} disabled={enriching}>
                          Retry enrichment
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Tags Section */}
            <BookTagsSection bookId={book.id} />

            {/* Content Warnings Section */}
            <ContentWarningsSection 
              bookId={book.id} 
              userHasRead={userBook?.shelf === 'read' || userBook?.shelf === 'unfinished'}
            />

            {/* Book Identity Lock Indicator */}
            {(book as any)?.identity_locked && (
              <Card className="border-muted bg-muted/50">
                <CardContent className="p-4 flex items-start gap-3">
                  <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Book Identity Locked</p>
                    <p className="text-xs text-muted-foreground">
                      This book's core details are locked because users have added ratings or reviews.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Wrong Book Warning */}
            {userBook && (
              <Card className="border-orange-500/50 bg-orange-500/5">
                <CardContent className="p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Wrong book?</p>
                    <p className="text-xs text-muted-foreground">If this isn't the right edition, you can fix it</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEditionMerge(true)}
                    >
                      Wrong Edition?
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowIdentityFixer(true)}
                    >
                      Fix Book
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* User's Interaction with Book */}
            {user && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle>
                    {!userBook ? 'Add to Library' : 
                     userBook.shelf === 'to-read' ? 'To Be Read' :
                     userBook.shelf === 'reading' ? 'Currently Reading' :
                     userBook.shelf === 'unfinished' ? 'Did Not Finish' : 'My Rating'}
                  </CardTitle>
                  <div className="flex gap-2">
                    {userBook && userBook.shelf === 'read' && !editing && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(true)}
                        >
                          <Edit3 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteRating}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* No interaction yet */}
                  {!userBook && !editing ? (
                    <div className="text-center py-8 space-y-4">
                      <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
                        <SaveToTBRButton
                          isSaved={isSaved}
                          onToggle={toggleSave}
                          loading={tbrLoading}
                          size="default"
                          variant="outline"
                        />
                        <Button onClick={handleStartReading} variant="outline">
                          <BookOpen className="w-4 h-4 mr-2" />
                          Start Reading
                        </Button>
                        <Button onClick={() => setEditing(true)} variant="default">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Rating
                        </Button>
                        <Button onClick={() => setShowDNFDialog(true)} variant="ghost">
                          Did Not Finish
                        </Button>
                      </div>
                    </div>
                  ) : 
                  /* Book in TBR */
                  userBook && userBook.shelf === 'to-read' && !editing ? (
                    <TBRRowContent
                      noteText={userBook.review_text}
                      addedAt={userBook.created_at}
                      isPurchased={userBook.is_purchased}
                      onMarkAsReading={handleStartReading}
                      onRemove={handleRemoveFromTBR}
                      onTogglePurchased={handleTogglePurchased}
                    />
                  ) :
                  /* Currently Reading */
                  userBook && userBook.shelf === 'reading' && !editing ? (
                    <div className="space-y-4">
                      <Badge variant="default" className="mb-2">Currently Reading</Badge>
                      {userBook.started_at && (
                        <p className="text-sm text-muted-foreground">
                          Started on {new Date(userBook.started_at).toLocaleDateString()}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button onClick={() => setEditing(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Mark as Finished & Rate
                        </Button>
                        <Button onClick={() => setShowDNFDialog(true)} variant="outline">
                          Did Not Finish
                        </Button>
                      </div>
                    </div>
                  ) :
                  /* Did Not Finish */
                  userBook && userBook.shelf === 'unfinished' && !editing ? (
                    <UnfinishedRowContent
                      reasonText={userBook.review_text || undefined}
                      startedAt={userBook.started_at || undefined}
                      progress={userBook.reading_progress_percent || 0}
                      currentPage={userBook.current_page || undefined}
                      totalPages={userBook.total_pages || book?.page_count || undefined}
                      onMarkAsReading={handleStartReading}
                      onMarkAsRead={() => setEditing(true)}
                      onRemove={handleDeleteRating}
                      loading={saving}
                    />
                  ) : editing ? (
                    <div className="space-y-4">
                      {/* Star Rating */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Star Rating *</label>
                        <StarRating value={rating} onChange={setRating} size="lg" />
                      </div>

                      {/* Review */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Review (Optional)</label>
                        <div className="space-y-3">
                          <Textarea
                            value={review}
                            onChange={(e) => setReview(e.target.value)}
                            placeholder="Share your thoughts about this book..."
                            rows={4}
                          />
                          
                          {/* Voice Review (Optional) */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Switch
                                id="use-voice-review"
                                checked={useVoiceReview}
                                onCheckedChange={setUseVoiceReview}
                              />
                              <Label htmlFor="use-voice-review">Use voice to draft review (optional)</Label>
                            </div>
                            {useVoiceReview && (
                              <VoiceReview
                                bookTitle={book?.title}
                                bookAuthor={book?.authors?.join(', ')}
                                onReviewGenerated={(generatedReview) => setReview(generatedReview)}
                              />
                            )}
                          </div>
                        </div>
                      </div>

      {/* Date Started */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Date Started (Optional)</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                            >
                              <Calendar className="mr-2 h-4 w-4" />
                              {startedAt ? format(startedAt, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={startedAt}
                              onSelect={(date) => {
                                setStartedAt(date);
                                if (date) setStartedMonth(date);
                              }}
                              month={startedMonth}
                              onMonthChange={setStartedMonth}
                              disabled={(date) => date > new Date()}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Date Finished */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Date Finished (Optional)</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                            >
                              <Calendar className="mr-2 h-4 w-4" />
                              {finishedAt ? format(finishedAt, 'PPP') : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={finishedAt}
                              onSelect={(date) => {
                                setFinishedAt(date);
                                if (date) setFinishedMonth(date);
                              }}
                              month={finishedMonth}
                              onMonthChange={setFinishedMonth}
                              disabled={(date) => {
                                const isFuture = date > new Date();
                                const isBeforeStart = startedAt && date < startedAt;
                                return isFuture || isBeforeStart;
                              }}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Mark as Read Toggle */}
                      {!finishedAt && !readAt.trim() && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="markAsRead"
                            checked={markAsRead}
                            onChange={(e) => setMarkAsRead(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor="markAsRead" className="text-sm font-medium">
                            Mark as finished today
                          </label>
                        </div>
                      )}

                      {/* Tag Selector */}
                      <TagSelector
                        selectedTags={selectedTags}
                        onChange={setSelectedTags}
                        maxTags={5}
                      />

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleSaveRating} disabled={saving || (rating === 0 && !review.trim())}>
                          {saving ? 'Saving...' : 'Save Rating'}
                        </Button>
                        <Button variant="outline" onClick={() => setEditing(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : userBook ? (
                    <div className="space-y-4">
                      {/* Display Rating */}
                      <div className="flex items-center gap-4">
                        <StarRating value={rating} readonly size="lg" />
                        <span className="text-lg font-semibold">{rating.toFixed(1)}</span>
                      </div>

                      {/* Display Review */}
                      {userBook.review_text && (
                        <div>
                          <h4 className="font-medium mb-2">My Review</h4>
                          <p className="text-muted-foreground leading-relaxed">{userBook.review_text}</p>
                        </div>
                      )}

                      {/* Display Dates */}
                      {(userBook.started_at || userBook.finished_at) && (
                        <div className="text-sm text-muted-foreground space-y-1">
                          {userBook.started_at && (
                            <div>Started: {new Date(userBook.started_at).toLocaleDateString()}</div>
                          )}
                          {userBook.finished_at && (
                            <div>Finished: {new Date(userBook.finished_at).toLocaleDateString()}</div>
                          )}
                        </div>
                      )}
                      
                      {/* Fallback for legacy read_at field */}
                      {!userBook.finished_at && userBook.read_at && (
                        <div className="text-sm text-muted-foreground">
                          Read on {new Date(userBook.read_at).toLocaleDateString()}
                        </div>
                      )}

                       {/* Privacy Badge (read-only, based on profile) */}
                       <PrivacyIndicator 
                         privacy={userBook.privacy as 'public' | 'private'} 
                         size="sm"
                       />

                       {/* Share Button for User's Review */}
                       {userBook.privacy === 'public' && userBook.review_text && (
                         <div className="pt-2">
                           <ShareButton
                             shareData={{
                               kind: 'review',
                               book_id: book.id,
                               review_id: userBook.id,
                               book: {
                                 id: book.id,
                                 title: book.title,
                                 authors: book.authors,
                                 cover_url: book.cover_url
                               },
                               review: {
                                 id: userBook.id,
                                 text: userBook.review_text,
                                 rating: userBook.rating_star
                               }
                             }}
                             variant="outline"
                             size="sm"
                           />
                         </div>
                       )}
                     </div>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* Community Reviews */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Community Reviews
                </CardTitle>
                {stats && <BookReviewStats stats={stats} />}
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Filters and sorting */}
                {filteredReviews.length > 0 && (
                  <BookReviewFilters
                    filterRating={reviewFilter}
                    onFilterChange={setReviewFilter}
                    sortBy={reviewSort}
                    onSortChange={setReviewSort}
                    totalReviews={stats?.totalRatings || 0}
                    filteredCount={filteredReviews.length}
                    ratingDistribution={stats?.ratingDistribution}
                  />
                )}
                
                {/* Review list using ReviewCard components */}
                {reviewsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading reviews...
                  </div>
                ) : filteredReviews.length > 0 ? (
                  <div className="space-y-4">
                    {filteredReviews.map((review) => (
                      <div key={review.id} id={`review-${review.id}`}>
                        <ReviewCard
                          review={review}
                          showBookInfo={false}
                        />
                      </div>
                    ))}
                  </div>
                ) : stats && stats.totalRatings > 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No reviews match your filters. Try adjusting them.
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No reviews yet. Be the first to review this book!
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Pairwise comparison sheet */}
      {pairwiseData && book && pairwiseData.comparison && (
        <PairwiseSheet
          isOpen={!!pairwiseData}
          onClose={clearPairwiseData}
          currentBook={{
            id: book.id,
            title: book.title,
            authors: book.authors,
            cover_url: book.cover_url
          }}
          tier={pairwiseData.tier}
          candidates={[pairwiseData.comparison.candidateBook]}
          placementState={pairwiseData.placementState}
          comparison={pairwiseData.comparison}
          onComplete={() => {
            clearPairwiseData();
          }}
        />
      )}

      {/* Book Identity Fixer Modal */}
      {book?.id && book?.title && (
        <BookIdentityFixer
          bookId={book.id}
          bookTitle={book.title}
          open={showIdentityFixer}
          onOpenChange={setShowIdentityFixer}
          onFixed={() => {
            setShowIdentityFixer(false);
            loadBookData();
          }}
        />
      )}

      {/* Edition Merge Dialog */}
      {book?.id && book?.title && (
        <EditionMergeDialog
          isOpen={showEditionMerge}
          onClose={() => setShowEditionMerge(false)}
          currentBookId={book.id}
          currentBookTitle={book.title}
          onMerge={(newBookId) => {
            navigate(`/book/${newBookId}`);
          }}
        />
      )}

      {/* Mark as Unfinished Dialog */}
      <MarkAsUnfinishedDialog
        open={showDNFDialog}
        onOpenChange={setShowDNFDialog}
        onConfirm={handleMarkAsUnfinished}
        bookTitle={book?.title || ''}
        totalPages={book?.page_count}
        currentPage={userBook?.current_page}
        loading={saving}
      />

      {/* Add Page Count Dialog */}
      {book && (
        <AddPageCountDialog
          open={showAddPageCount}
          onOpenChange={setShowAddPageCount}
          bookId={book.id}
          bookTitle={book.title}
          onPageCountAdded={loadBookData}
        />
      )}
    </div>
  );
}